import { doc, getDoc, setDoc, updateDoc, Timestamp, collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';

/** Monta o petEntry omitindo galeria quando null/undefined */
const buildPetEntry = (data, idOverride) => {
  const entry = {
    id: idOverride ?? data.id,
    nome: data.nome || '',
    raca: data.raca || '',
    tutor: data.tutor || '',
    telefone: data.telefone || '',
    criadoEm: data.criadoEm || null,
    atualizadoEm: data.atualizadoEm || null
  };
  if (data.galeria != null) entry.galeria = data.galeria;
  if (data.planoMensal !== undefined) {
    entry.planoMensal = data.planoMensal;
    if (entry.planoMensal === true) entry.diasAgendados = data.diasAgendados || [];
  }
  return entry;
};

/**
 * Atualiza o agregado de pets quando um pet é criado
 */
export const updatePetsAggregationOnCreate = async (petData) => {
  try {
    if (!petData || !petData.id) {
      // Dados do pet inválidos para atualizar agregado
      return;
    }

    const petsAggregateRef = doc(db, 'agregadoPets', 'all');
    const petsAggregateDoc = await getDoc(petsAggregateRef);
    
    // Criar entrada do pet com apenas os campos necessários para o agregado
    // Novo pet: não incluir planoMensal no agregado (só incluir quando estiver definido em petData)
    const petEntry = buildPetEntry(petData);
    
    if (!petsAggregateDoc.exists()) {
      // Se o agregado não existe, buscar todos os pets e criar o agregado
      const allPetsSnapshot = await getDocs(collection(db, 'pets'));
      const allPets = allPetsSnapshot.docs.map(d => buildPetEntry(d.data(), d.id));
      
      const existingPetIndex = allPets.findIndex(p => p.id === petData.id);
      const petEntryForAll = buildPetEntry(petData);
      if (existingPetIndex === -1) {
        allPets.push(petEntryForAll);
      } else {
        allPets[existingPetIndex] = petEntryForAll;
      }
      
      // Ordenar por nome
      allPets.sort((a, b) => {
        const nameA = (a.nome || '').toUpperCase();
        const nameB = (b.nome || '').toUpperCase();
        return nameA.localeCompare(nameB, 'pt-BR');
      });
      
      await setDoc(petsAggregateRef, {
        pets: allPets,
        quantidadeTotal: allPets.length,
        ultimaAtualizacao: Timestamp.now()
      });
      return;
    }

    const currentData = petsAggregateDoc.data();
    const pets = currentData.pets || [];
    
    // Verificar se o pet já existe (não deveria, mas por segurança)
    const existingIndex = pets.findIndex(p => p.id === petData.id);
    
    if (existingIndex === -1) {
      // Adicionar novo pet e ordenar por nome
      pets.push(petEntry);
      pets.sort((a, b) => {
        const nameA = (a.nome || '').toUpperCase();
        const nameB = (b.nome || '').toUpperCase();
        return nameA.localeCompare(nameB, 'pt-BR');
      });
      
      await updateDoc(petsAggregateRef, {
        pets: pets,
        quantidadeTotal: pets.length,
        ultimaAtualizacao: Timestamp.now()
      });
    } else {
      // Se já existe, atualizar (caso de sincronização)
      pets[existingIndex] = petEntry;
      pets.sort((a, b) => {
        const nameA = (a.nome || '').toUpperCase();
        const nameB = (b.nome || '').toUpperCase();
        return nameA.localeCompare(nameB, 'pt-BR');
      });
      
      await updateDoc(petsAggregateRef, {
        pets: pets,
        quantidadeTotal: pets.length,
        ultimaAtualizacao: Timestamp.now()
      });
    }
  } catch (error) {
    // Erro ao atualizar agregado de pets na criação
    throw error; // Re-throw para que o erro seja visível
  }
};

/**
 * Atualiza o agregado de pets quando um pet é editado
 */
export const updatePetsAggregationOnUpdate = async (petData) => {
  try {
    if (!petData || !petData.id) {
      // Dados do pet inválidos para atualizar agregado
      return;
    }

    // Iniciando atualização para pet
    
    const petsAggregateRef = doc(db, 'agregadoPets', 'all');
    const petsAggregateDoc = await getDoc(petsAggregateRef);
    
    if (!petsAggregateDoc.exists()) {
      // Se o agregado não existe, buscar todos os pets e criar o agregado
      const allPetsSnapshot = await getDocs(collection(db, 'pets'));
      const allPets = allPetsSnapshot.docs.map(d => buildPetEntry(d.data(), d.id));
      
      const existingPetIndex = allPets.findIndex(p => p.id === petData.id);
      const petEntryForAll = buildPetEntry(petData);
      if (existingPetIndex === -1) {
        allPets.push(petEntryForAll);
      } else {
        allPets[existingPetIndex] = petEntryForAll;
      }
      
      // Usar allPets diretamente (já filtrado)
      const filteredPets = allPets;
      
      // Ordenar por nome
      filteredPets.sort((a, b) => {
        const nameA = (a.nome || '').toUpperCase();
        const nameB = (b.nome || '').toUpperCase();
        return nameA.localeCompare(nameB, 'pt-BR');
      });
      
      await setDoc(petsAggregateRef, {
        pets: filteredPets,
        quantidadeTotal: filteredPets.length,
        ultimaAtualizacao: Timestamp.now()
      });
      return;
    }

    const currentData = petsAggregateDoc.data();
    const pets = currentData.pets || [];
    
    // Total de pets no agregado
    
    // Encontrar e atualizar o pet
    const petIndex = pets.findIndex(p => p.id === petData.id);
    
    if (petIndex !== -1) {
      pets[petIndex] = buildPetEntry(petData);
      
      // Reordenar por nome
      pets.sort((a, b) => {
        const nameA = (a.nome || '').toUpperCase();
        const nameB = (b.nome || '').toUpperCase();
        return nameA.localeCompare(nameB, 'pt-BR');
      });
      
      // Atualizando documento do agregado
      await updateDoc(petsAggregateRef, {
        pets: pets,
        quantidadeTotal: pets.length,
        ultimaAtualizacao: Timestamp.now()
      });
      // Documento atualizado com sucesso
    } else {
      pets.push(buildPetEntry(petData));
      pets.sort((a, b) => {
        const nameA = (a.nome || '').toUpperCase();
        const nameB = (b.nome || '').toUpperCase();
        return nameA.localeCompare(nameB, 'pt-BR');
      });
      
      await updateDoc(petsAggregateRef, {
        pets: pets,
        quantidadeTotal: pets.length,
        ultimaAtualizacao: Timestamp.now()
      });
      // Pet adicionado ao agregado
    }
  } catch (error) {
    // Erro ao atualizar agregado de pets na edição
    throw error; // Re-throw para que o erro seja visível
  }
};

/**
 * Atualiza o agregado de pets quando um pet é excluído
 */
export const updatePetsAggregationOnDelete = async (idPet) => {
  try {
    if (!idPet) {
      // ID do pet inválido para atualizar agregado
      return;
    }

    // Iniciando exclusão para pet
    
    const petsAggregateRef = doc(db, 'agregadoPets', 'all');
    const petsAggregateDoc = await getDoc(petsAggregateRef);
    
    if (!petsAggregateDoc.exists()) {
      // Se o agregado não existe, não há nada para deletar
      // Criar agregado vazio (ou buscar todos os pets e remover o deletado)
      const allPetsSnapshot = await getDocs(collection(db, 'pets'));
      const allPets = allPetsSnapshot.docs
        .map(d => buildPetEntry(d.data(), d.id))
        .filter(p => p.id !== idPet); // Remover o pet deletado
      
      // Ordenar por nome
      allPets.sort((a, b) => {
        const nameA = (a.nome || '').toUpperCase();
        const nameB = (b.nome || '').toUpperCase();
        return nameA.localeCompare(nameB, 'pt-BR');
      });
      
      await setDoc(petsAggregateRef, {
        pets: allPets,
        quantidadeTotal: allPets.length,
        ultimaAtualizacao: Timestamp.now()
      });
      return;
    }

    const currentData = petsAggregateDoc.data();
    const pets = currentData.pets || [];
    
    // Total de pets no agregado antes
    
    // Remover o pet
    const filteredPets = pets.filter(p => p.id !== idPet);
    
    // Total de pets após filtro
    
    // Verificar se realmente removeu algum pet
    if (filteredPets.length !== pets.length) {
      // Atualizando documento do agregado
      await updateDoc(petsAggregateRef, {
        pets: filteredPets,
        quantidadeTotal: filteredPets.length,
        ultimaAtualizacao: Timestamp.now()
      });
      // Documento atualizado com sucesso
    } else {
      // Pet não encontrado no agregado para exclusão
    }
  } catch (error) {
    // Erro ao atualizar agregado de pets na exclusão
    throw error; // Re-throw para que o erro seja visível
  }
};

